import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { useAuth } from '../AuthContext';
import api from '../api';
import { formatMoani } from '../utils/moaniUtils';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  MonetizationOn as MoneyIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

function AuctionRefunds() {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [processingRefunds, setProcessingRefunds] = useState(new Set());
  const [refundedTransactions, setRefundedTransactions] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Fetch failed auction transactions
  const { data: refundData, isLoading, error, refetch } = useQuery({
    queryKey: ['auctionRefunds', currentPage, pageSize],
    queryFn: async () => {
      const response = await api.get(`/auction-refunds?page=${currentPage}&limit=${pageSize}`);
      return response.data;
    },
    refetchInterval: 30000,
    staleTime: 10000,
    keepPreviousData: true
  });

  // Refund mutation
  const refundMutation = useMutation({
    mutationFn: async ({ transactionId, userId, amount, reason }) => {
      const response = await api.post('/auction-refunds/refund', {
        transactionId,
        userId,
        amount,
        reason
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      setProcessingRefunds(prev => {
        const newSet = new Set(prev);
        newSet.delete(variables.transactionId);
        return newSet;
      });
      // Immediately mark as refunded in UI
      setRefundedTransactions(prev => new Set(prev).add(variables.transactionId));
      // Also refresh data for accurate counts
      queryClient.invalidateQueries(['auctionRefunds']);
    },
    onError: (error, variables) => {
      setProcessingRefunds(prev => {
        const newSet = new Set(prev);
        newSet.delete(variables.transactionId);
        return newSet;
      });
      console.error('Refund failed:', error);
    }
  });

  const handleProcessRefund = (transaction) => {
    setProcessingRefunds(prev => new Set(prev).add(transaction.transaction_id));
    
    const defaultReason = `Admin refund for failed auction transaction - ${transaction.asset_title || 'Unknown Item'} (TX: ${transaction.transaction_id})`;
    
    refundMutation.mutate({
      transactionId: transaction.transaction_id,
      userId: transaction.userId,
      amount: transaction.amount,
      reason: defaultReason
    });
  };


  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Check if user has super admin privileges
  if (!user || user.role !== 'super_admin') {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Super Admin privileges required.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading failed auction transactions...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load auction refunds: {error.message}
        <Button onClick={() => refetch()} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  const { summary = {}, transactions = [], pagination = {} } = refundData || {};

  // Filter out transactions that were just refunded in this session
  const visibleTransactions = transactions.filter(tx => 
    !refundedTransactions.has(tx.transaction_id)
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Auction Refunds
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={() => refetch()} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #ff5252 0%, #f44336 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <WarningIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography color="white" variant="h6">
                    {summary.totalUnrefundedTransactions || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    Need Refunds
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <MoneyIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography color="white" variant="h6">
                    {formatMoani(summary.totalUnrefundedAmount || '0')} MOANI
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    Still Owed
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography color="white" variant="h6">
                    {summary.totalRefundedTransactions || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    Already Refunded
                  </Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.6)">
                    {formatMoani(summary.totalRefundedAmount || '0')} MOANI
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <PersonIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography color="white" variant="h6">
                    {summary.uniqueUnrefundedUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    Users Need Refunds
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transactions Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Failed Auction Transactions
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {visibleTransactions.length === 0 ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon />
              <Typography>No failed auction transactions found!</Typography>
            </Stack>
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Item</strong></TableCell>
                  <TableCell><strong>Amount Lost</strong></TableCell>
                  <TableCell><strong>Auction ID</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTransactions.map((transaction) => (
                  <TableRow key={transaction.transaction_id}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transaction.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography 
                          component="a"
                          href={`/user/${transaction.userId}/details`}
                          variant="body2" 
                          fontWeight="medium"
                          sx={{ 
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.dark' }
                          }}
                          onClick={(event) => selectUserById(transaction.userId, navigate)}
                          title="Click to view user details (Ctrl+click for new tab)"
                        >
                          {transaction.user_name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.user_email}
                        </Typography>
                        {transaction.user_wallet && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {`${transaction.user_wallet.slice(0, 6)}...${transaction.user_wallet.slice(-4)}`}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {transaction.asset_title || 'Unknown Item'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Asset ID: {transaction.assetId || 'N/A'}
                        </Typography>
                        {transaction.stackSize && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Quantity: {transaction.stackSize}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        color="error.main"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {formatMoani(transaction.amount)} MOANI
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {transaction.auction_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.has_been_refunded ? (
                        <Box>
                          <Chip 
                            label="Refunded" 
                            color="success" 
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(transaction.refunded_at)}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip 
                          label="Needs Refund" 
                          color="error" 
                          size="small"
                          icon={<WarningIcon />}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.has_been_refunded ? (
                        <Chip 
                          label="Already Refunded" 
                          color="default" 
                          size="small"
                          disabled
                        />
                      ) : (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={processingRefunds.has(transaction.transaction_id) ? 
                            <CircularProgress size={16} color="inherit" /> : 
                            <MoneyIcon />
                          }
                          onClick={() => handleProcessRefund(transaction)}
                          disabled={processingRefunds.has(transaction.transaction_id)}
                        >
                          {processingRefunds.has(transaction.transaction_id) ? 'Processing...' : 'Refund'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Pagination Controls */}
        {transactions.length > 0 && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.page || 1) - 1) * (pagination.limit || pageSize) + 1}-{Math.min((pagination.page || 1) * (pagination.limit || pageSize), pagination.total || 0)} of {pagination.total || 0} transactions
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Per Page"
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {pagination.totalPages > 1 && (
              <Pagination
                count={pagination.totalPages || 1}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Success/Error Messages */}
      {refundMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to process refund: {refundMutation.error?.response?.data?.error || refundMutation.error?.message}
        </Alert>
      )}
      
      {refundMutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Refund processed successfully! {refundMutation.data?.message}
        </Alert>
      )}
    </Box>
  );
}

export default AuctionRefunds;
